import { useEffect, useState } from "react";
import { SearchableDropdown } from "../shared/SearchableDropdown";
import "../styles/modal.css";
import { getCommerceSalesYears, upsertCommerceDimTableItem } from "../services/dimension-service";
import { useGetToken } from "../hooks/custom-hooks";
import toast from "react-hot-toast";
import { parseNewDropdownValue } from "../utilities/commerceConfig";

export const EditDimensionModal = ({
  isOpen,
  dimension,
  config,
  onClose
}) => {
  const [values, setValues] = useState({});
  const [salesYears, setSalesYears] = useState([]);
  const [isSalesYearsLoading, setIsSalesYearsLoading] = useState(false);
  const token = useGetToken();

  useEffect(() => {
    const supportedEntities = ["ProductItem"];

    if (!isOpen || !token || !dimension?.key || !supportedEntities.includes(dimension.entity)) {
      setSalesYears([]);
      return;
    }

    let isCurrent = true;

    const loadSalesYears = async () => {
      setIsSalesYearsLoading(true);
      const response = await getCommerceSalesYears(dimension.entity, dimension.key, token);

      if (isCurrent) {
        setSalesYears(response.data?.years ?? []);
        setIsSalesYearsLoading(false);
      }
    };

    loadSalesYears();

    return () => {
      isCurrent = false;
    };
  }, [dimension?.entity, dimension?.key, isOpen, token]);

 useEffect(() => {
  if (!isOpen || !dimension || !config) return;

  const initialValues = {};

  config.fields?.forEach((field) => {
    if (field.type === "multiSelectDropdown") {
      initialValues[field.key] = field.selectedLookup ?? [];
      return;
    }

    const parentKey = field.parentKey ?? "fields";

    const initialValue =
      dimension[parentKey]?.[field.key] ??
      dimension[field.key] ??
      "";

    initialValues[field.key] =
      field.type === "lookup" || field.type === "dropdown"
        ? initialValue === null || initialValue === undefined
          ? ""
          : String(initialValue)
        : initialValue;
  });

  setValues(initialValues);
}, [isOpen, dimension?.id, config]);

  if (!isOpen || !dimension || !config) return null;

const handleChange = (key, value) => {
  setValues((prev) => {
    const next = {
      ...prev,
      [key]: value
    };

    config.fields?.forEach((field) => {
      if (
        field.visibleWhenField === key &&
        value !== field.visibleWhenValue
      ) {
        next[field.key] = "";
      }
    });

    return next;
  });
};

  const toArray = (value) => {
    if (value === null || value === undefined || value === "") return [];
    return Array.isArray(value) ? value : [value];
  };


const normalizeObject = (obj) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      typeof value === "string" && value.trim() === "" ? null : value,
    ])
  );

  const handleUpsert = () => {
    const invalidField = config.fields?.find((field) => {
      if (!field.visibleWhenField) {
        return false;
      }

      const shouldBeVisible =
        values[field.visibleWhenField] === field.visibleWhenValue;

      const value = values[field.key];

      const isEmpty =
        value === null ||
        value === undefined ||
        value === "";

      return shouldBeVisible && isEmpty;
    });

    if (invalidField) {
      toast.error(`${invalidField.label} is required.`);
      return;
    }

    const fields = {
      ...(dimension.fields ?? {}),
    };

    const relationships = [];

    config.fields?.forEach((field) => {
      let value = values[field.key];

      if (field.type === "boolean" && value === "") {
        value = false;
      }

      const newValue = parseNewDropdownValue(value);

      if (newValue) {
        fields[field.key] = null;

        const displayField =
          field.displayField ??
          field.labelField ??
          field.key.replace(/Key$/, "");

        fields[displayField] = newValue.label;

        return;
      }

      if (field.type === "multiSelectDropdown") {
        relationships.push(
          ...(Array.isArray(value) ? value : value ? [value] : [])
        );

        return;
      }

      fields[field.key] = value;
    });

    const payload = {
      entity: dimension.entity,
      tableName: dimension.tableName,
      primaryKey: dimension.primaryKey,
      rows: [
        {
          fields: normalizeObject(fields),
          relationships: relationships,
        },
      ],
    };
    updateDim(payload);
  };


  const updateDim = async (payload) => {
    const response = await upsertCommerceDimTableItem(payload, token);
    if (response.data) {
      toast.success("Dimension Updated.");
      onClose(true);
    } else {
      toast.error("The update did not save. Please refresh the page and try again.");
    }
  };

  const primaryValue =
    config.displayValue?.(dimension) ??
    dimension.name ??
    dimension.label ??
    dimension.id;

  const showsSalesYears = ["ProductItem"].includes(dimension.entity);

  const isFieldVisible = (field) => {
    if (!field.visibleWhenField) {
      return true;
    }

    return values[field.visibleWhenField] === field.visibleWhenValue;
  };

  const visibleFields = config.fields?.filter(isFieldVisible) ?? [];

  const standardFields = visibleFields.filter(
    (field) =>
      field.type !== "multiSelectDropdown"
  );

  const relationshipFields = visibleFields.filter(
    (field) => field.type === "multiSelectDropdown"
  );
  const topVendors = dimension?.topVendors ?? [];
  const hasTopVendors = topVendors.length > 0;

  const hasRelationshipFields = relationshipFields.length > 0;
  const hasRightPanel = hasRelationshipFields || hasTopVendors;
  return (
    <div className="dim-modal-overlay">
      <div className="dim-modal dim-modal-large">
        <button className="dim-modal-close" onClick={onClose}>
          x
        </button>

        <div className="dim-modal-header">
          <h2>{config.title ?? "Edit"}</h2>
          <p>{config.description ?? "Update details and manage related items."}</p>
        </div>

        <div  className={`dim-modal-content ${
          hasRightPanel ? "dim-modal-content--split" : "dim-modal-content--single"
        }`}>
          <div className="dim-modal-main">
            <section className="dim-modal-card">
              <label className="dim-section-label">
                {config.displayLabel ?? "Name"}
              </label>

              <div className="dim-preview-field">
                {primaryValue}
              </div>

              {showsSalesYears && (
                <div className="dim-sales-years">
                  <span className="dim-sales-years-label">Years Sold</span>
                  <span className="dim-sales-years-value">
                    {isSalesYearsLoading
                      ? "Loading..."
                      : salesYears.length
                        ? salesYears.join(" · ")
                        : "No recorded sales"}
                  </span>
                </div>
              )}

              <div className="dim-divider" />

              <div className="dim-field-grid">
                {standardFields.map((field) => {
                  if (field.type === "boolean") {
                    return (
                      <label key={field.key} className="dim-choice-card">
                        <input
                          type="checkbox"
                          checked={!!values[field.key]}
                          onChange={(e) =>
                            handleChange(field.key, e.target.checked)
                          }
                        />
                        <div>
                          <strong>{field.label}</strong>
                          {field.description && <p>{field.description}</p>}
                        </div>
                      </label>
                    );
                  }

                  if (field.type === "dropdown" || field.type === "text" || field.type === "lookup") {
                    return (
                    <SearchableDropdown
                      className="searchable-dropdown dark-dropdown"
                      id={field.key}
                      label={field.label}
                      value={values[field.key] ?? ""}
                      dropdownValues={field.lookup ?? []}
                      defaultPhrase={field.placeholder}
                      multiple={false}
                      createdLabel={field.displayField ? values[field.displayField] : null}
                      handleDropdownChange={(e) => {
                        handleChange(field.key, e.target.value);

                        if (e.target.createdLabel && field.key.endsWith("Key")) {
                          handleChange(field.key.replace(/Key$/, ""), e.target.createdLabel);
                        }
                      }}
                      creatable={field.creatable === true}
                    />
                    );
                  }
                  return null;
                })}
              </div>
            </section>
          </div>
          {relationshipFields.map((field) => {
            const selectedItems = toArray(values[field.key]);
            const selectedOptions =
              field.lookup?.filter((option) =>
                selectedItems.includes(option.key)
              ) ?? [];

            return (
              <aside key={field.key} className="dim-modal-side">
                <div className="dim-section-header">
                  <div>
                    <h3>
                      {field.relationshipLabel ??
                        field.label ??
                        "Associated Items"}{" "}
                      ({selectedOptions.length})
                    </h3>
                    <p>
                      These {field.associatedEntityLabel?.toLowerCase() ?? "items"} are associated with this dimension.
                    </p>
                  </div>
                </div>

                <SearchableDropdown
                  className="searchable-dropdown dark-dropdown compact-dropdown"
                  id={field.key}
                  label=""
                  value={values[field.key] ?? []}
                  dropdownValues={field.lookup ?? []}
                  defaultPhrase={field.placeholder ?? "Search associated items..."}
                  multiple
                  compact
                  handleDropdownChange={(e) =>
                    handleChange(field.key, e.target.value)
                  }
                />

                <div className="dim-associated-list">
                  {selectedOptions.length === 0 && (
                    <p className="dim-empty-text">
                      No items selected.
                    </p>
                  )}

                  {selectedOptions.slice(0, 6).map((item) => (
                    <div key={item.key} className="dim-associated-row">
                      <label className="dim-associated-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.key)}
                          onChange={() => {
                            const next = selectedItems.includes(item.key)
                              ? selectedItems.filter((k) => k !== item.key)
                              : [...selectedItems, item.key];

                            handleChange(field.key, next);
                          }}
                        />

                        <span>{item.label}</span>
                      </label>
                    </div>
                  ))}

                  {selectedOptions.length > 6 && (
                    <div className="dim-associated-more">
                      + {selectedOptions.length - 6} more items
                    </div>
                  )}
                </div>
              </aside>
            );
          })}
          {hasTopVendors && (
            <aside className="dim-modal-side">
              <div className="dim-section-header">
                <div>
                  <h3>Top Vendors</h3>
                  <p>Top 5 vendors where this item is sold.</p>
                </div>
              </div>

              <div className="dim-associated-list">
                {topVendors.map((vendor, index) => (
                  <div
                    key={vendor.key}
                    className="dim-associated-row"
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        width: "100%"
                      }}
                    >
                      <strong>{index + 1}.</strong>

                      <div style={{ flex: 1 }}>
                        {vendor.vendorName}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </aside>
          )}
        </div>

        <div className="dim-modal-actions">
          <button className="dim-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="dim-btn-primary" onClick={handleUpsert}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
